import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { API_URL } from '../config/api';
import { MdCheckCircle, MdError, MdHourglassEmpty, MdAssessment, MdCreditCard } from 'react-icons/md';

function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying your payment...');
  const [reportId, setReportId] = useState(null);
  const pollingRef = useRef(null);
  const attemptsRef = useRef(0);

  const paymentType = searchParams.get('type'); // 'candidacy', 'subscription', or null (poll)
  const isCandidacy = paymentType === 'candidacy';
  const isSubscription = paymentType === 'subscription';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session. Please try again.');
      return;
    }

    if (isSubscription) {
      activateSubscription(sessionId);
    } else {
      pollPaymentStatus(sessionId);
    }

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activateSubscription = async (sessionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription/activate/${sessionId}`);
      if (response.data.activated) {
        setStatus('success');
        setMessage('Your representative subscription has been activated!');
      } else {
        setStatus('error');
        setMessage(response.data.reason || 'Subscription activation pending. Please try again shortly.');
      }
    } catch (error) {
      console.error('Error activating subscription:', error);
      setStatus('error');
      setMessage('Failed to activate subscription. Please contact support.');
    }
  };

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attemptsRef.current >= maxAttempts) {
      setStatus('timeout');
      setMessage(isCandidacy
        ? 'Payment verification is taking longer than expected. Please check your candidacy reports.'
        : 'Payment verification is taking longer than expected. Please check your transactions page.');
      return;
    }

    try {
      if (isCandidacy) {
        // Candidacy report payment flow
        const response = await axios.get(`${API_URL}/api/candidacy/payment-status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid' && data.report_created) {
          setStatus('success');
          setReportId(data.report_id);
          setMessage('Payment successful! Your candidacy assessment is being generated.');
          return;
        } else if (data.payment_status === 'expired') {
          setStatus('error');
          setMessage('Payment session expired. Please try again.');
          return;
        } else {
          attemptsRef.current += 1;
          setMessage('Processing your payment...');
          pollingRef.current = setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
        }
      } else {
        // Standard poll payment flow
        const response = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Payment successful! Your poll is now live.');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          setMessage('Payment session expired. Please try again.');
          return;
        } else {
          attemptsRef.current += 1;
          setMessage('Processing your payment...');
          pollingRef.current = setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      attemptsRef.current += 1;
      
      if (attemptsRef.current >= maxAttempts) {
        setStatus('error');
        setMessage('Failed to verify payment. Please check your transactions page.');
      } else {
        pollingRef.current = setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
      }
    }
  };

  const renderSubscriptionSuccess = () => (
    <div className="text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <MdCreditCard className="text-green-500 text-5xl" />
      </div>
      <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Subscription Activated!</h1>
      <p className="text-gray-600 font-rubik mb-6">{message}</p>
      <div className="bg-blue-50 border border-blue-200 rounded-card p-4 mb-6 text-left">
        <p className="text-sm text-blue-800 font-rubik">
          <strong>You&apos;re all set!</strong><br/>
          Your representative profile subscription is now active. You&apos;ll receive email notifications before each renewal.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => navigate('/profile')}
          className="btn-primary px-6 py-3"
          data-testid="go-to-profile-btn"
        >
          View Profile
        </button>
        <button
          onClick={() => navigate('/transactions?tab=subscriptions')}
          className="btn-secondary px-6 py-3"
          data-testid="view-subscription-btn"
        >
          View Subscription
        </button>
      </div>
    </div>
  );

  const renderCandidacySuccess = () => (
    <div className="text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <MdAssessment className="text-green-500 text-5xl" />
      </div>
      <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Payment Successful!</h1>
      <p className="text-gray-600 font-rubik mb-6">{message}</p>
      <div className="bg-blue-50 border border-blue-200 rounded-card p-4 mb-6 text-left">
        <p className="text-sm text-blue-800 font-rubik">
          <strong>What&apos;s next?</strong><br/>
          Your candidacy assessment is being processed in the background. You&apos;ll receive a notification when the report is ready to view.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {reportId && (
          <button
            onClick={() => navigate(`/candidacy-report/${reportId}`)}
            className="btn-primary px-6 py-3"
            data-testid="view-report-btn"
          >
            View Report
          </button>
        )}
        <button
          onClick={() => navigate('/transactions?tab=candidacy')}
          className="btn-secondary px-6 py-3"
          data-testid="view-candidacy-reports-btn"
        >
          View All Reports
        </button>
      </div>
    </div>
  );

  const renderPollSuccess = () => (
    <div className="text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <MdCheckCircle className="text-green-500 text-5xl" />
      </div>
      <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Payment Successful!</h1>
      <p className="text-gray-600 font-rubik mb-6">{message}</p>
      <div className="bg-amber-50 border border-amber-200 rounded-card p-4 mb-6 text-left">
        <p className="text-sm text-amber-800 font-rubik">
          <strong>What&apos;s next?</strong><br/>
          Your poll is now live! Other users can vote on it right away.
          You&apos;ll be able to view results and demographics from the My Polls page.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => navigate('/my-polls')}
          className="btn-primary px-6 py-3"
          data-testid="view-my-polls-btn"
        >
          View My Polls
        </button>
        <button
          onClick={() => navigate('/transactions')}
          className="btn-secondary px-6 py-3"
          data-testid="view-transactions-btn"
        >
          View Transactions
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (status) {
      case 'success':
        if (isSubscription) return renderSubscriptionSuccess();
        return isCandidacy ? renderCandidacySuccess() : renderPollSuccess();

      case 'error':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MdError className="text-red-500 text-5xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Payment Failed</h1>
            <p className="text-gray-600 font-rubik mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate(isCandidacy ? '/transactions?tab=candidacy' : '/my-polls')}
                className="btn-primary px-6 py-3"
                data-testid="try-again-btn"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/transactions')}
                className="btn-secondary px-6 py-3"
                data-testid="view-transactions-error-btn"
              >
                View Transactions
              </button>
            </div>
          </div>
        );

      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MdHourglassEmpty className="text-amber-500 text-5xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Processing</h1>
            <p className="text-gray-600 font-rubik mb-6">{message}</p>
            <button
              onClick={() => navigate(isCandidacy ? '/transactions?tab=candidacy' : '/transactions')}
              className="btn-primary px-6 py-3"
              data-testid="check-transactions-btn"
            >
              {isCandidacy ? 'Check Reports' : 'Check Transactions'}
            </button>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 font-rubik mb-3">Verifying Payment</h1>
            <p className="text-gray-600 font-rubik">{message}</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="bg-white rounded-card shadow-lg p-8 max-w-md w-full">
        {renderContent()}
      </div>
    </div>
  );
}

export default PaymentSuccessPage;
